import diagnostics from "@/services/diagnostics";
import realtime from "@/services/realtime";
import { getIceServers } from "@/services/iceConfig";

/**
 * WebRTC-сессия одной игры: сигналинг через realtime (CONTRACT §3.2) + контроль соединения.
 *
 *   const peer = createPeerSession({
 *     gameUUID, initiator,
 *     onRemoteStream: (stream) => …,           // поток соперника
 *     onState: (state) => …,                   // 'new'|'connecting'|'connected'|'disconnected'|'failed'|'closed'
 *     onProgress: (stage) => …,                // 'peer'|'offer'|'remote'|'connected' — для линии прогресса
 *     onConnectTimeout: () => …,               // за 30 c так и не соединились (QUALITY_FAILED уже отправлен)
 *     onConnectionLost: () => …,               // связь пропала и не восстановилась (ICE restart не помог)
 *   })                                         // ← подписка на сигналинг происходит СРАЗУ (до await'ов)
 *   await peer.start(localStream)              // создаёт RTCPeerConnection, добавляет треки, offer / request_offer
 *   peer.close()                               // отписка, таймеры, pc.close(), pc = null
 *
 * Правила:
 *  - инициатор отправляет offer сразу; повторяет ТЕКУЩИЙ offer по {type:'request_offer'} или если
 *    answer не пришёл за 5 c (до 3 попыток); не-инициатор после подписки шлёт request_offer
 *    (и повторяет, пока offer не пришёл, до 3 раз);
 *  - ICE-кандидаты буферизуются до установки remoteDescription;
 *  - connectionState 'connected' → quality_report QUALITY_OK (один раз за игру);
 *    не соединились за 30 c → quality_report QUALITY_FAILED + onConnectTimeout;
 *  - 'disconnected' дольше 8 c или 'failed' → инициатор делает один ICE restart;
 *    через 15 c всё ещё нет связи → onConnectionLost (экран отправляет leave_game);
 *  - входящие сообщения сигналинга обрабатываются строго последовательно.
 */

export const RTC_TIMING = Object.freeze({
  OFFER_RESEND_MS: 5000,
  OFFER_MAX_TRIES: 3,
  CONNECT_TIMEOUT_MS: 30000,
  DISCONNECT_GRACE_MS: 8000,
  RECOVERY_TIMEOUT_MS: 15000,
});

/** Громкость звука соперника. */
export const REMOTE_VOLUME = 0.3;

const SIGNAL_TYPES = ["sdp_offer", "sdp_answer", "ice_candidate", "request_offer"];

const isDev = process.env.NODE_ENV === "development";
const log = (...args) => {
  if (isDev) console.log("[webrtc]", ...args);
};

/** {type, sdp} из строки или объекта (совместимо со старым клиентом, который слал RTCSessionDescription). */
function toDescription(raw, fallbackType) {
  if (!raw) return null;
  if (typeof raw === "string") return { type: fallbackType, sdp: raw };
  if (typeof raw.sdp === "string") return { type: raw.type || fallbackType, sdp: raw.sdp };
  return null;
}

/** Строка o= из SDP (идентификатор и версия сессии) или весь SDP, если строки нет. */
function originLine(sdp) {
  const match = /^o=.*$/m.exec(sdp || "");
  return match ? match[0].trim() : sdp;
}

function plainDescription(description) {
  return description ? { type: description.type, sdp: description.sdp } : null;
}

function plainCandidate(candidate) {
  if (!candidate) return null;
  if (typeof candidate.toJSON === "function") return candidate.toJSON();
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment,
  };
}

/** Состояние соединения с фолбэком на iceConnectionState (старый Safari без connectionState). */
function effectiveState(pc) {
  if (pc.connectionState) return pc.connectionState;
  switch (pc.iceConnectionState) {
    case "checking":
      return "connecting";
    case "connected":
    case "completed":
      return "connected";
    case "disconnected":
      return "disconnected";
    case "failed":
      return "failed";
    case "closed":
      return "closed";
    default:
      return "new";
  }
}

/**
 * @param {object} options
 * @param {string} options.gameUUID
 * @param {boolean} options.initiator
 * @returns {{start:(stream:MediaStream)=>Promise<void>, close:()=>void, readonly state:string, readonly connected:boolean}}
 */
export function createPeerSession(options) {
  const { gameUUID, initiator } = options;

  const call = (name, payload) => {
    const fn = options[name];
    if (typeof fn !== "function") return;
    try {
      fn(payload);
    } catch (error) {
      console.error(`[webrtc] ${name} failed`, error);
    }
  };

  let pc = null;
  let closed = false;
  let releaseDiagnostics = () => {};
  let started = false;
  let chain = Promise.resolve();
  const early = [];
  const pendingCandidates = [];
  let remoteStream = null;
  let state = "new";

  let offerTries = 0;
  let offerTimer = null;
  let requestTries = 0;
  let requestTimer = null;
  let connectTimer = null;
  let graceTimer = null;
  let recoveryTimer = null;

  let everConnected = false;
  let restartUsed = false;
  let qualityOkSent = false;
  let qualityFailedSent = false;

  function send(message, sendOptions) {
    return realtime.send({ ...message, gameUUID }, sendOptions);
  }

  function enqueue(message) {
    if (closed || !message) return;
    if (message.gameUUID && message.gameUUID !== gameUUID) return;
    if (!started) {
      early.push(message);
      return;
    }
    run(() => handle(message));
  }

  /** Последовательное выполнение операций над pc (без гонок setRemoteDescription/createAnswer). */
  function run(task) {
    chain = chain
      .then(() => (closed || !pc ? null : task()))
      .catch((error) => log("signaling error", error && error.message ? error.message : error));
    return chain;
  }

  // Подписка ДО создания RTCPeerConnection: ранние offer/ICE не теряются.
  const offs = SIGNAL_TYPES.map((type) => realtime.on(type, enqueue));
  offs.push(
    realtime.on("reconnected", () => {
      if (closed || !started) return;
      // Сообщения могли потеряться во время обрыва — повторяем свою часть рукопожатия.
      if (initiator) run(() => resendOffer(true));
      else if (pc && !pc.remoteDescription) sendRequestOffer(true);
    }),
  );

  /* ---------------------------------------------------------- offer/answer */

  async function makeOffer(iceRestart) {
    if (typeof pc.restartIce === "function" && iceRestart) pc.restartIce();
    const offer = await pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
    if (closed || !pc) return;
    await pc.setLocalDescription(offer);
    offerTries = 0;
    sendCurrentOffer(false);
  }

  /** Отправить текущий offer (pc.localDescription — после сбора включает и ICE-кандидаты). */
  function sendCurrentOffer(force) {
    if (closed || !pc || pc.signalingState !== "have-local-offer" || !pc.localDescription) return;
    if (!force && offerTries >= RTC_TIMING.OFFER_MAX_TRIES) return;
    offerTries += 1;
    send({ type: "sdp_offer", sdp: plainDescription(pc.localDescription) });
    call("onProgress", "offer");
    log(`offer sent (#${offerTries})`);
    clearTimeout(offerTimer);
    offerTimer = null;
    if (offerTries < RTC_TIMING.OFFER_MAX_TRIES) {
      offerTimer = setTimeout(() => run(() => sendCurrentOffer(false)), RTC_TIMING.OFFER_RESEND_MS);
    }
  }

  async function resendOffer(force) {
    if (!pc) return;
    if (pc.signalingState === "have-local-offer") sendCurrentOffer(force);
  }

  function sendRequestOffer(force) {
    if (closed || !pc || pc.remoteDescription) return;
    if (!force && requestTries >= RTC_TIMING.OFFER_MAX_TRIES) return;
    requestTries += 1;
    send({ type: "request_offer" });
    call("onProgress", "offer");
    clearTimeout(requestTimer);
    requestTimer = null;
    if (requestTries < RTC_TIMING.OFFER_MAX_TRIES) {
      requestTimer = setTimeout(() => sendRequestOffer(false), RTC_TIMING.OFFER_RESEND_MS);
    }
  }

  async function flushCandidates() {
    if (!pc || !pc.remoteDescription) return;
    const list = pendingCandidates.splice(0, pendingCandidates.length);
    for (const candidate of list) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (error) {
        log("buffered candidate rejected", error && error.message);
      }
    }
  }

  async function onOffer(message) {
    if (initiator) return; // офферы создаёт только инициатор — glare невозможен
    const description = toDescription(message.sdp, "offer");
    if (!description) return;
    clearTimeout(requestTimer);
    requestTimer = null;
    // Повтор того же offer'а, на который мы уже ответили, — просто повторяем answer.
    // Сравниваем строку o= (id и версия сессии): повторно отправленный offer может уже содержать кандидаты.
    if (
      pc.signalingState === "stable" &&
      pc.remoteDescription &&
      pc.localDescription &&
      originLine(pc.remoteDescription.sdp) === originLine(description.sdp)
    ) {
      send({ type: "sdp_answer", sdp: plainDescription(pc.localDescription) });
      return;
    }
    await pc.setRemoteDescription(description);
    call("onProgress", "remote");
    await flushCandidates();
    const answer = await pc.createAnswer();
    if (closed || !pc) return;
    await pc.setLocalDescription(answer);
    send({ type: "sdp_answer", sdp: plainDescription(pc.localDescription) });
    log("answer sent");
  }

  async function onAnswer(message) {
    if (!initiator) return;
    if (pc.signalingState !== "have-local-offer") return; // дубликат answer
    const description = toDescription(message.sdp, "answer");
    if (!description) return;
    await pc.setRemoteDescription(description);
    clearTimeout(offerTimer);
    offerTimer = null;
    call("onProgress", "remote");
    await flushCandidates();
    log("answer applied");
  }

  async function onCandidate(message) {
    const candidate = message.candidate;
    if (!candidate || (typeof candidate === "object" && !candidate.candidate)) return;
    const init = typeof candidate === "string" ? { candidate, sdpMid: "0", sdpMLineIndex: 0 } : candidate;
    if (!pc.remoteDescription) {
      pendingCandidates.push(init);
      return;
    }
    try {
      await pc.addIceCandidate(init);
    } catch (error) {
      // Кандидат от следующего (ICE restart) описания пришёл раньше него — применим позже.
      pendingCandidates.push(init);
    }
  }

  async function handle(message) {
    switch (message.type) {
      case "sdp_offer":
        await onOffer(message);
        break;
      case "sdp_answer":
        await onAnswer(message);
        break;
      case "ice_candidate":
        await onCandidate(message);
        break;
      case "request_offer":
        if (initiator) await resendOffer(true);
        break;
      default:
        break;
    }
  }

  /* --------------------------------------------------- состояние соединения */

  function sendQuality(status) {
    send({ type: "quality_report", status }, { ttl: 60000 });
  }

  function clearRecovery() {
    clearTimeout(graceTimer);
    clearTimeout(recoveryTimer);
    graceTimer = null;
    recoveryTimer = null;
  }

  function beginRecovery() {
    if (closed || recoveryTimer) return;
    clearTimeout(graceTimer);
    graceTimer = null;
    if (initiator && !restartUsed) {
      restartUsed = true;
      log("ICE restart");
      run(() => makeOffer(true));
    }
    recoveryTimer = setTimeout(() => {
      recoveryTimer = null;
      if (closed || !pc || effectiveState(pc) === "connected") return;
      // До первого соединения решает таймаут подключения (QUALITY_FAILED).
      if (everConnected) call("onConnectionLost");
    }, RTC_TIMING.RECOVERY_TIMEOUT_MS);
  }

  function onConnectionChange() {
    if (!pc) return;
    const next = effectiveState(pc);
    if (next === state) return;
    state = next;
    log("state", next);
    call("onState", next);
    if (next === "connected") {
      clearRecovery();
      clearTimeout(connectTimer);
      connectTimer = null;
      restartUsed = false;
      everConnected = true;
      call("onProgress", "connected");
      if (!qualityOkSent && !qualityFailedSent) {
        qualityOkSent = true;
        sendQuality("QUALITY_OK");
      }
    } else if (next === "disconnected") {
      if (!graceTimer && !recoveryTimer) {
        graceTimer = setTimeout(() => {
          graceTimer = null;
          beginRecovery();
        }, RTC_TIMING.DISCONNECT_GRACE_MS);
      }
    } else if (next === "failed") {
      beginRecovery();
    }
  }

  /* ---------------------------------------------------------------- API */

  /**
   * Создать RTCPeerConnection, добавить локальные треки и начать рукопожатие.
   * @param {MediaStream|null} localStream
   */
  async function start(localStream) {
    if (closed || started) return;
    pc = new RTCPeerConnection({ iceServers: getIceServers() });
    releaseDiagnostics = diagnostics.registerPeer(pc);
    pc.onicecandidate = (event) => {
      if (event.candidate) send({ type: "ice_candidate", candidate: plainCandidate(event.candidate) });
    };
    pc.ontrack = (event) => {
      let stream = event.streams && event.streams[0];
      if (!stream) {
        if (!remoteStream) remoteStream = new MediaStream();
        remoteStream.addTrack(event.track);
        stream = remoteStream;
      }
      remoteStream = stream;
      call("onRemoteStream", stream);
    };
    pc.onconnectionstatechange = onConnectionChange;
    pc.oniceconnectionstatechange = onConnectionChange;
    if (localStream) {
      localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));
    }
    call("onProgress", "peer");

    connectTimer = setTimeout(() => {
      connectTimer = null;
      if (closed || everConnected) return;
      log("connect timeout");
      qualityFailedSent = true;
      sendQuality("QUALITY_FAILED");
      call("onConnectTimeout");
    }, RTC_TIMING.CONNECT_TIMEOUT_MS);

    started = true;
    if (initiator) {
      await run(() => makeOffer(false));
    } else {
      sendRequestOffer(true);
    }
    // Сообщения, пришедшие до создания pc, — по порядку.
    early.splice(0, early.length).forEach((message) => run(() => handle(message)));
  }

  /** Закрыть сессию (идемпотентно). */
  function close() {
    if (closed) return;
    closed = true;
    releaseDiagnostics();
    offs.forEach((off) => off());
    clearTimeout(offerTimer);
    clearTimeout(requestTimer);
    clearTimeout(connectTimer);
    clearRecovery();
    early.length = 0;
    pendingCandidates.length = 0;
    realtime.dropQueued((type) => SIGNAL_TYPES.includes(type));
    if (pc) {
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      try {
        pc.close();
      } catch (e) {
        // уже закрыт
      }
      pc = null;
    }
    remoteStream = null;
    state = "closed";
  }

  return {
    start,
    close,
    get state() {
      return state;
    },
    get connected() {
      return state === "connected";
    },
  };
}

export default createPeerSession;
